import { ChartData, ChartOptions } from 'chart.js';

/**
 * Chart helpers shared by the attendance page and the big-screen dashboard graph
 * widgets, so both plot the same time series with the same look. Mirrors the
 * dataset/options shape built in AttendanceComponent.updateChart /
 * buildChartOptions.
 *
 * A "timed entry" is the normalised per-gate/scanner struct the attendance page
 * produces: { gate, timestamps[], entries[], exits[], reentries[], internal[],
 * inVenue[] }.
 */
export interface TimedEntry {
  gate: string;
  timestamps: number[];
  formattedTimestamps: string[];
  entries: number[];
  exits: number[];
  reentries: number[];
  internal: number[];
  inVenue: number[];
}

/**
 * Builds line-chart data for the named gates/scanners from their timed entries.
 * Each name becomes one dataset (one line); values are aligned to the shared
 * sorted timeline so lines share an x-axis. Colours cycle through `colors`.
 */
export function buildTimedChartData(
  timedEntries: TimedEntry[],
  names: string[],
  colors: string[],
  fill: boolean
): ChartData {
  const timestamps = Array.from(new Set(timedEntries[0]?.timestamps ?? []))
    .slice()
    .sort((a, b) => a - b);
  const labels = timestamps.map((ts) => {
    const idx = timedEntries[0]?.timestamps.indexOf(ts) ?? -1;
    return idx !== -1 ? timedEntries[0]?.formattedTimestamps[idx] ?? '' : '';
  });

  const datasets = names
    .map((name, i) => {
      const entry = timedEntries.find((e) => e.gate === name);
      if (!entry) return null;
      const data = timestamps.map((ts) => {
        const idx = entry.timestamps.indexOf(ts);
        return idx !== -1 ? entry.entries[idx] : 0;
      });
      return {
        label: name,
        data,
        borderColor: colors[i % colors.length],
        fill,
        tension: 0.4,
        customMetrics: {
          exits: entry.exits || [],
          reentries: entry.reentries || [],
          internal: entry.internal || [],
          inVenue: entry.inVenue || [],
        },
      };
    })
    .filter((d): d is NonNullable<typeof d> => d !== null);

  return { labels, datasets };
}

/** Theme-aware line-chart options matching the attendance page's graph. */
export function buildChartOptions(isDark: boolean): ChartOptions {
  const textColor = isDark ? '#e2e8f0' : '#1f2328';
  const mutedTextColor = isDark ? '#94a3b8' : '#475569';
  const gridColor = isDark ? 'rgba(148, 163, 184, 0.15)' : 'rgba(15, 23, 42, 0.08)';
  const tooltipBg = isDark ? 'rgba(15, 23, 42, 0.95)' : 'rgba(255, 255, 255, 0.95)';
  const tooltipText = isDark ? '#f8fafc' : '#0f172a';
  const tooltipBorder = isDark ? '#334155' : '#e6e8ec';

  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        labels: { color: textColor },
      },
      tooltip: {
        backgroundColor: tooltipBg,
        titleColor: tooltipText,
        bodyColor: tooltipText,
        borderColor: tooltipBorder,
        borderWidth: 1,
        callbacks: {
          label: (context: { dataset: any; dataIndex: any }) => {
            const dataset = context.dataset as any;
            const index = context.dataIndex;
            const entries = dataset.data[index];
            const exits = dataset.customMetrics?.exits[index] ?? 0;
            const reentries = dataset.customMetrics?.reentries[index] ?? 0;
            return [`Entries: ${entries}`, `Exits: ${exits}`, `Reentries: ${reentries}`];
          },
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: 'Time', color: mutedTextColor },
        ticks: { color: mutedTextColor },
        grid: { color: gridColor },
      },
      y: {
        title: { display: true, text: 'Entries', color: mutedTextColor },
        ticks: { color: mutedTextColor },
        grid: { color: gridColor },
      },
    },
  } as ChartOptions;
}
